import { useRef, type ReactNode } from "react";
import { Accordion as AccordionRadix } from "radix-ui";
import { ChevronDown } from "lucide-react";
import styles, { OPEN_ANIMATION_MS } from "./Accordion.css";

interface AccordionItem {
  value: string;
  trigger: string;
  content: ReactNode;
}

interface AccordionProps {
  items: AccordionItem[];
}

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const Accordion = ({ items }: AccordionProps) => {
  const itemRefs = useRef(new Map<string, HTMLDivElement>());

  const scrollOpenedItemIntoView = (value: string) => {
    if (!value) return;
    window.setTimeout(() => {
      itemRefs.current.get(value)?.scrollIntoView({
        behavior: prefersReducedMotion() ? "auto" : "smooth",
        block: "nearest",
      });
    }, OPEN_ANIMATION_MS);
  };

  return (
    <AccordionRadix.Root
      className={styles.root}
      type="single"
      collapsible
      onValueChange={scrollOpenedItemIntoView}
    >
      {items.map((item) => (
        <AccordionRadix.Item
          key={item.value}
          value={item.value}
          className={styles.item}
          ref={(node) => {
            if (node) itemRefs.current.set(item.value, node);
            else itemRefs.current.delete(item.value);
          }}
        >
          <AccordionRadix.Header>
            <AccordionRadix.Trigger className={styles.trigger}>
              {item.trigger}
              <ChevronDown size={20} className={styles.icon} />
            </AccordionRadix.Trigger>
          </AccordionRadix.Header>
          <AccordionRadix.Content className={styles.content}>
            <div className={styles.contentInner}>{item.content}</div>
          </AccordionRadix.Content>
        </AccordionRadix.Item>
      ))}
    </AccordionRadix.Root>
  );
};

export default Accordion;
